import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount, getTenant } from '../../../theme/tokens';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DownloadIcon, ChevronDownIcon } from 'lucide-react';
import { DateRangePicker } from './DateRangePicker';
import { GranularitySelector, Granularity, getAvailableGranularities, getDefaultGranularity } from './GranularitySelector';
import { PaymentMethodsFilters } from './PaymentMethodsFilters';
interface DateRange {
  start: Date;
  end: Date;
}
interface PaymentMethodsTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
type KeyMetric = 'share-of-volume' | 'share-of-payment-count' | 'average-order-value';
type PivotOption = 'Payment method' | 'Card brand' | 'Payment method category' | 'Funding source type';
// Color palette - enough for up to 7 categories
const COLOR_PALETTE = ['#4F46E5', '#6B7280', '#059669', '#D97706', '#94A3B8', '#DC2626', '#7C3AED' // violet-600
];
// Pivot breakdown data with share percentages for chart generation
const PIVOT_CONFIG: Record<PivotOption, {
  key: string;
  label: string;
  volumeShare: number;
  aovBase: number;
}[]> = {
  'Payment method': [{
    key: 'Card',
    label: 'Card',
    volumeShare: 0.725,
    aovBase: 290
  }, {
    key: 'ApplePay',
    label: 'Apple Pay',
    volumeShare: 0.128,
    aovBase: 325
  }, {
    key: 'GooglePay',
    label: 'Google Pay',
    volumeShare: 0.07,
    aovBase: 265
  }, {
    key: 'PromptPay',
    label: 'PromptPay',
    volumeShare: 0.044,
    aovBase: 185
  }, {
    key: 'Others',
    label: 'Others',
    volumeShare: 0.033,
    aovBase: 245
  }],
  'Card brand': [{
    key: 'Visa',
    label: 'Visa',
    volumeShare: 0.484,
    aovBase: 275
  }, {
    key: 'Mastercard',
    label: 'Mastercard',
    volumeShare: 0.178,
    aovBase: 295
  }, {
    key: 'Amex',
    label: 'American Express',
    volumeShare: 0.037,
    aovBase: 420
  }, {
    key: 'JCB',
    label: 'JCB',
    volumeShare: 0.02,
    aovBase: 310
  }, {
    key: 'UnionPay',
    label: 'UnionPay',
    volumeShare: 0.007,
    aovBase: 230
  }, {
    key: 'OthersNonCard',
    label: 'Others (non-card)',
    volumeShare: 0.274,
    aovBase: 195
  }],
  'Payment method category': [{
    key: 'Cards',
    label: 'Cards',
    volumeShare: 0.725,
    aovBase: 290
  }, {
    key: 'Wallets',
    label: 'Wallets',
    volumeShare: 0.198,
    aovBase: 315
  }, {
    key: 'BankTransfers',
    label: 'Bank transfers',
    volumeShare: 0.051,
    aovBase: 180
  }, {
    key: 'Others',
    label: 'Others',
    volumeShare: 0.026,
    aovBase: 245
  }],
  'Funding source type': [{
    key: 'Card',
    label: 'Card',
    volumeShare: 0.725,
    aovBase: 290
  }, {
    key: 'BankAccount',
    label: 'Bank account',
    volumeShare: 0.095,
    aovBase: 185
  }, {
    key: 'StoredBalance',
    label: 'Stored balance',
    volumeShare: 0.151,
    aovBase: 260
  }, {
    key: 'BNPLCredit',
    label: 'BNPL credit',
    volumeShare: 0.028,
    aovBase: 380
  }]
};
// Country color palette
const COUNTRY_COLORS = ['#4F46E5', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0891B2', '#6B7280'];
type CountryMetric = 'country-volume' | 'country-count' | 'country-aov';
const COUNTRY_DATA = [{
  key: 'MY',
  flag: '🇲🇾',
  label: 'Maldives',
  volume: 8420000,
  count: 28940,
  aov: 291,
  volumeShare: 0.582,
  countShare: 0.601,
  change: '+5.2%'
}, {
  key: 'US',
  flag: '🇺🇸',
  label: 'United States',
  volume: 2180000,
  count: 6820,
  aov: 320,
  volumeShare: 0.151,
  countShare: 0.142,
  change: '+12.4%'
}, {
  key: 'GB',
  flag: '🇬🇧',
  label: 'United Kingdom',
  volume: 1260000,
  count: 3950,
  aov: 319,
  volumeShare: 0.087,
  countShare: 0.082,
  change: '+8.1%'
}, {
  key: 'SG',
  flag: '🇸🇬',
  label: 'Singapore',
  volume: 890000,
  count: 2810,
  aov: 317,
  volumeShare: 0.062,
  countShare: 0.058,
  change: '+15.2%'
}, {
  key: 'DE',
  flag: '🇩🇪',
  label: 'Germany',
  volume: 640000,
  count: 2100,
  aov: 305,
  volumeShare: 0.044,
  countShare: 0.044,
  change: '+3.8%'
}, {
  key: 'AU',
  flag: '🇦🇺',
  label: 'Australia',
  volume: 530000,
  count: 1740,
  aov: 305,
  volumeShare: 0.037,
  countShare: 0.036,
  change: '+6.5%'
}, {
  key: 'Other',
  flag: '🌐',
  label: 'Others',
  volume: 530000,
  count: 1780,
  aov: 298,
  volumeShare: 0.037,
  countShare: 0.037,
  change: '+2.1%'
}];
const OTHER_COUNTRIES = [{
  flag: '🇯🇵',
  label: 'Japan',
  volume: 142000,
  volumeShare: 0.01,
  count: 480,
  countShare: 0.01,
  aov: 296,
  change: '+4.2%'
}, {
  flag: '🇭🇰',
  label: 'Hong Kong',
  volume: 118000,
  volumeShare: 0.008,
  count: 395,
  countShare: 0.008,
  aov: 299,
  change: '+3.1%'
}, {
  flag: '🇳🇿',
  label: 'New Zealand',
  volume: 95000,
  volumeShare: 0.007,
  count: 320,
  countShare: 0.007,
  aov: 297,
  change: '+1.8%'
}, {
  flag: '🇨🇦',
  label: 'Canada',
  volume: 82000,
  volumeShare: 0.006,
  count: 275,
  countShare: 0.006,
  aov: 298,
  change: '+2.5%'
}, {
  flag: '🇫🇷',
  label: 'France',
  volume: 53000,
  volumeShare: 0.004,
  count: 178,
  countShare: 0.004,
  aov: 298,
  change: '+0.9%'
}, {
  flag: '🇳🇱',
  label: 'Netherlands',
  volume: 40000,
  volumeShare: 0.003,
  count: 132,
  countShare: 0.003,
  aov: 303,
  change: '+1.4%'
}];
export function PaymentMethodsTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: PaymentMethodsTabProps) {
  const {
    t
  } = useTranslation('analytics');
  const [selectedMetric, setSelectedMetric] = useState<KeyMetric>('share-of-volume');
  const [chartData, setChartData] = useState<any[]>([]);
  // Filter states
  const [paymentMethodCategories, setPaymentMethodCategories] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [fundingSourceTypes, setFundingSourceTypes] = useState<string[]>([]);
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [inputMethods, setInputMethods] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [networkTokenStatuses, setNetworkTokenStatuses] = useState<string[]>([]);
  // Pivot state
  const [pivotBy, setPivotBy] = useState<PivotOption>('Payment method');
  const [showPivotDropdown, setShowPivotDropdown] = useState(false);
  const pivotRef = useRef<HTMLDivElement>(null);
  // Country metric state
  const [selectedCountryMetric, setSelectedCountryMetric] = useState<CountryMetric>('country-volume');
  const [showOtherCountries, setShowOtherCountries] = useState(false);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pivotRef.current && !pivotRef.current.contains(event.target as Node)) {
        setShowPivotDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Current pivot categories (excluding Total)
  const pivotCategories = PIVOT_CONFIG[pivotBy];
  // Color map for current pivot
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    pivotCategories.forEach((cat, i) => {
      map[cat.key] = COLOR_PALETTE[i % COLOR_PALETTE.length];
    });
    return map;
  }, [pivotBy]);
  // Generate mock chart data based on selected metric AND pivot
  useEffect(() => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const data = [];
    let baseValue = 0;
    let variance = 0;
    if (selectedMetric === 'share-of-volume') {
      baseValue = 500000;
      variance = 100000;
    } else if (selectedMetric === 'share-of-payment-count') {
      baseValue = 1500;
      variance = 300;
    } else {
      baseValue = 290;
      variance = 10;
    }
    for (let i = 0; i <= days; i++) {
      const date = new Date(dateRange.start);
      date.setDate(date.getDate() + i);
      const total = Math.max(0, baseValue + (Math.random() - 0.5) * variance);
      if (selectedMetric === 'average-order-value') {
        const point: any = {
          date: date.toISOString()
        };
        pivotCategories.forEach(cat => {
          point[cat.key] = cat.aovBase + (Math.random() - 0.5) * 20;
        });
        data.push(point);
      } else {
        const point: any = {
          date: date.toISOString()
        };
        pivotCategories.forEach(cat => {
          point[cat.key] = total * cat.volumeShare * (0.95 + Math.random() * 0.1);
        });
        data.push(point);
      }
    }
    setChartData(data);
  }, [dateRange, granularity, selectedMetric, pivotBy]);
  const metrics = [{
    id: 'share-of-volume',
    title: 'Share of volume',
    value: `${getTenant().currency.code} 14.06Mn`,
    change: '+8.70%',
    tooltipText: 'Total volume processed through selected payment methods.'
  }, {
    id: 'share-of-payment-count',
    title: 'Share of payment count',
    value: '48,490',
    change: '+6.38%',
    tooltipText: 'Total number of successful payments through selected payment methods.'
  }, {
    id: 'average-order-value',
    title: 'Average order value',
    value: `${getTenant().currency.code} 290.12`,
    change: '+2.15%',
    tooltipText: 'Average transaction amount for selected payment methods.'
  }];
  const pivotData: Record<PivotOption, any[]> = {
    'Payment method': [{
      category: 'Card',
      volume: `${getTenant().currency.code} 10.2Mn`,
      share: '72.5%',
      change: '+7.2%',
      paymentCount: '35,120',
      countShare: '72.4%',
      aov: `${getTenant().currency.code} 290.50`
    }, {
      category: 'Apple Pay',
      volume: `${getTenant().currency.code} 1.8Mn`,
      share: '12.8%',
      change: '+15.3%',
      paymentCount: '6,230',
      countShare: '12.9%',
      aov: `${getTenant().currency.code} 289.00`
    }, {
      category: 'Google Pay',
      volume: `${getTenant().currency.code} 980K`,
      share: '7.0%',
      change: '+12.1%',
      paymentCount: '3,410',
      countShare: '7.0%',
      aov: `${getTenant().currency.code} 287.40`
    }, {
      category: 'PromptPay',
      volume: `${getTenant().currency.code} 620K`,
      share: '4.4%',
      change: '+22.5%',
      paymentCount: '2,180',
      countShare: '4.5%',
      aov: `${getTenant().currency.code} 284.40`
    }, {
      category: 'Others',
      volume: `${getTenant().currency.code} 460K`,
      share: '3.3%',
      change: '+3.8%',
      paymentCount: '1,550',
      countShare: '3.2%',
      aov: `${getTenant().currency.code} 296.80`
    }, {
      category: 'Total',
      volume: `${getTenant().currency.code} 14.06Mn`,
      share: '100.00%',
      change: '',
      paymentCount: '48,490',
      countShare: '100.00%',
      aov: `${getTenant().currency.code} 290.12`,
      isTotal: true
    }],
    'Card brand': [{
      category: 'Visa',
      volume: `${getTenant().currency.code} 6.8Mn`,
      share: '48.4%',
      change: '+6.8%',
      paymentCount: '23,400',
      countShare: '48.3%',
      aov: `${getTenant().currency.code} 290.60`
    }, {
      category: 'Mastercard',
      volume: `${getTenant().currency.code} 2.5Mn`,
      share: '17.8%',
      change: '+8.1%',
      paymentCount: '8,620',
      countShare: '17.8%',
      aov: `${getTenant().currency.code} 290.00`
    }, {
      category: 'American Express',
      volume: `${getTenant().currency.code} 520K`,
      share: '3.7%',
      change: '+4.2%',
      paymentCount: '1,800',
      countShare: '3.7%',
      aov: `${getTenant().currency.code} 288.90`
    }, {
      category: 'JCB',
      volume: `${getTenant().currency.code} 280K`,
      share: '2.0%',
      change: '+3.5%',
      paymentCount: '960',
      countShare: '2.0%',
      aov: `${getTenant().currency.code} 291.70`
    }, {
      category: 'UnionPay',
      volume: `${getTenant().currency.code} 100K`,
      share: '0.7%',
      change: '+11.2%',
      paymentCount: '340',
      countShare: '0.7%',
      aov: `${getTenant().currency.code} 294.10`
    }, {
      category: 'Others (non-card)',
      volume: `${getTenant().currency.code} 3.86Mn`,
      share: '27.4%',
      change: '+15.8%',
      paymentCount: '13,370',
      countShare: '27.6%',
      aov: `${getTenant().currency.code} 288.70`
    }, {
      category: 'Total',
      volume: `${getTenant().currency.code} 14.06Mn`,
      share: '100.00%',
      change: '',
      paymentCount: '48,490',
      countShare: '100.00%',
      aov: `${getTenant().currency.code} 290.12`,
      isTotal: true
    }],
    'Payment method category': [{
      category: 'Cards',
      volume: `${getTenant().currency.code} 10.2Mn`,
      share: '72.5%',
      change: '+7.2%',
      paymentCount: '35,120',
      countShare: '72.4%',
      aov: `${getTenant().currency.code} 290.50`
    }, {
      category: 'Wallets',
      volume: `${getTenant().currency.code} 2.78Mn`,
      share: '19.8%',
      change: '+14.1%',
      paymentCount: '9,640',
      countShare: '19.9%',
      aov: `${getTenant().currency.code} 288.40`
    }, {
      category: 'Bank transfers',
      volume: `${getTenant().currency.code} 720K`,
      share: '5.1%',
      change: '+18.3%',
      paymentCount: '2,530',
      countShare: '5.2%',
      aov: `${getTenant().currency.code} 284.60`
    }, {
      category: 'Others',
      volume: `${getTenant().currency.code} 360K`,
      share: '2.6%',
      change: '+5.2%',
      paymentCount: '1,200',
      countShare: '2.5%',
      aov: `${getTenant().currency.code} 300.00`
    }, {
      category: 'Total',
      volume: `${getTenant().currency.code} 14.06Mn`,
      share: '100.00%',
      change: '',
      paymentCount: '48,490',
      countShare: '100.00%',
      aov: `${getTenant().currency.code} 290.12`,
      isTotal: true
    }],
    'Funding source type': [{
      category: 'Card',
      volume: `${getTenant().currency.code} 10.2Mn`,
      share: '72.5%',
      change: '+7.2%',
      paymentCount: '35,120',
      countShare: '72.4%',
      aov: `${getTenant().currency.code} 290.50`
    }, {
      category: 'Bank account',
      volume: `${getTenant().currency.code} 1.34Mn`,
      share: '9.5%',
      change: '+19.8%',
      paymentCount: '4,710',
      countShare: '9.7%',
      aov: `${getTenant().currency.code} 284.50`
    }, {
      category: 'Stored balance',
      volume: `${getTenant().currency.code} 2.12Mn`,
      share: '15.1%',
      change: '+12.4%',
      paymentCount: '7,310',
      countShare: '15.1%',
      aov: `${getTenant().currency.code} 290.00`
    }, {
      category: 'BNPL credit',
      volume: `${getTenant().currency.code} 400K`,
      share: '2.8%',
      change: '+8.7%',
      paymentCount: '1,350',
      countShare: '2.8%',
      aov: `${getTenant().currency.code} 296.30`
    }, {
      category: 'Total',
      volume: `${getTenant().currency.code} 14.06Mn`,
      share: '100.00%',
      change: '',
      paymentCount: '48,490',
      countShare: '100.00%',
      aov: `${getTenant().currency.code} 290.12`,
      isTotal: true
    }]
  };
  const breakdownRows = pivotData[pivotBy];
  const countryMetrics = [{
    id: 'country-volume',
    title: 'Volume by country',
    value: `${getTenant().currency.code} 14.45Mn`,
    change: '+7.2%'
  }, {
    id: 'country-count',
    title: 'Payment count by country',
    value: '48,140',
    change: '+6.8%'
  }, {
    id: 'country-aov',
    title: 'Avg order value by country',
    value: `${getTenant().currency.code} 300`,
    change: '+2.3%'
  }];
  // Country bar chart data
  const countryChartData = useMemo(() => {
    return COUNTRY_DATA.map(country => ({
      name: country.label,
      flag: country.flag,
      value: selectedCountryMetric === 'country-volume' ? country.volume : selectedCountryMetric === 'country-count' ? country.count : country.aov
    })).sort((a, b) => b.value - a.value);
  }, [selectedCountryMetric]);
  const countryBarFormatter = (value: number) => {
    if (selectedCountryMetric === 'country-volume') {
      if (value >= 1000000) return `${getTenant().currency.code} ${(value / 1000000).toFixed(1)}Mn`;
      if (value >= 1000) return `${getTenant().currency.code} ${(value / 1000).toFixed(0)}K`;
      return `${getTenant().currency.code} ${value}`;
    }
    if (selectedCountryMetric === 'country-aov') {
      return `${getTenant().currency.code} ${value}`;
    }
    return value.toLocaleString();
  };
  const countryTooltipFormatter = (value: number) => {
    if (selectedCountryMetric === 'country-volume') {
      return formatCurrencyAmount(value, {
        fractionDigits: 0
      });
    }
    if (selectedCountryMetric === 'country-aov') {
      return formatCurrencyAmount(value, {
        fractionDigits: 2
      });
    }
    return value.toLocaleString();
  };
  const CountryBarTooltip = ({
    active,
    payload
  }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm" data-id="element-2069">
          <p className="font-medium text-gray-900 mb-1" data-id="element-2070">
            {data.flag} {data.name}
          </p>
          <p className="text-gray-600" data-id="element-2071">{countryTooltipFormatter(data.value)}</p>
        </div>;
    }
    return null;
  };
  // Country breakdown rows for table
  const countryBreakdownRows = [...COUNTRY_DATA.map(c => ({
    flag: c.flag,
    country: c.label,
    volume: c.volume >= 1000000 ? `${getTenant().currency.code} ${(c.volume / 1000000).toFixed(2)}Mn` : `${getTenant().currency.code} ${(c.volume / 1000).toFixed(0)}K`,
    volumeShare: `${(c.volumeShare * 100).toFixed(1)}%`,
    count: c.count.toLocaleString(),
    countShare: `${(c.countShare * 100).toFixed(1)}%`,
    aov: `${getTenant().currency.code} ${c.aov}`,
    change: c.change,
    isTotal: false
  })), {
    flag: '',
    country: 'Total',
    volume: `${getTenant().currency.code} 14.45Mn`,
    volumeShare: '100.0%',
    count: '48,140',
    countShare: '100.0%',
    aov: `${getTenant().currency.code} 300`,
    change: '+7.2%',
    isTotal: true
  }];
  const dateFormatter = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const yAxisFormatter = (value: any) => {
    if (selectedMetric === 'share-of-volume') {
      return value >= 1000000 ? `${(value / 1000000).toFixed(1)}M` : `${(value / 1000).toFixed(0)}k`;
    }
    if (selectedMetric === 'share-of-payment-count') {
      return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value;
    }
    return value.toFixed(0);
  };
  const tooltipValueFormatter = (value: any) => {
    if (selectedMetric === 'share-of-volume' || selectedMetric === 'average-order-value') {
      return `${getTenant().currency.code} ${value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
    return value.toLocaleString('en-US', {
      maximumFractionDigits: 0
    });
  };
  const handleDateRangeChange = (newRange: {
    start: Date;
    end: Date;
  }) => {
    onDateRangeChange(newRange);
    const newAvailable = getAvailableGranularities(newRange);
    if (!newAvailable.includes(granularity)) {
      onGranularityChange(getDefaultGranularity(newRange));
    }
  };
  const availableGranularities = getAvailableGranularities(dateRange);
  const isGranularityHidden = availableGranularities.length <= 1;
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm" data-id="element-2072">
          <p className="font-medium text-gray-900 mb-2" data-id="element-2073">
            {dateFormatter(label)}
          </p>
          {payload.map((entry: any, index: number) => <div key={index} className="flex items-center justify-between gap-4 mb-1" data-id="element-2074">
              <div className="flex items-center gap-2" data-id="element-2075">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-2076" />

                <span className="text-gray-600" data-id="element-2077">
                  {entry.name === 'value' ? 'Average order value' : entry.name}
                </span>
              </div>
              <span className="font-medium text-gray-900" data-id="element-2078">
                {tooltipValueFormatter(entry.value)}
              </span>
            </div>)}
        </div>;
    }
    return null;
  };
  return <div className="space-y-6" data-id="element-2079">
      {/* Main Filters Row */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-2080">
        <DateRangePicker value={dateRange} onChange={handleDateRangeChange} data-id="element-2081" />
        <GranularitySelector value={granularity} onChange={onGranularityChange} allowedGranularities={availableGranularities} hidden={isGranularityHidden} data-id="element-2082" />

        <PaymentMethodsFilters paymentMethodCategories={paymentMethodCategories} onPaymentMethodCategoriesChange={setPaymentMethodCategories} paymentMethods={paymentMethods} onPaymentMethodsChange={setPaymentMethods} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} fundingSourceTypes={fundingSourceTypes} onFundingSourceTypesChange={setFundingSourceTypes} cardTypes={cardTypes} onCardTypesChange={setCardTypes} inputMethods={inputMethods} onInputMethodsChange={setInputMethods} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} networkTokenStatuses={networkTokenStatuses} onNetworkTokenStatusesChange={setNetworkTokenStatuses} data-id="element-2083" />
      </div>

      {/* Chart + Breakdown unified section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-2084">
        <div className="p-6 pb-0" data-id="element-2085">
          <div className="flex items-center justify-between mb-4" data-id="element-2086">
            <div className="flex items-center gap-4" data-id="element-2087">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-2088">
                {t('components.PaymentMethodsTab.payment_methods_overview')}
              </h2>
              <div className="flex items-center gap-2" data-id="element-2089">
                <span className="text-sm text-gray-500" data-id="element-2090">
                  {t('components.CardPaymentsBreakdown.pivot_by')}
                </span>
                <div className="relative" ref={pivotRef} data-id="element-2091">
                  <button onClick={() => setShowPivotDropdown(!showPivotDropdown)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2092">
                    <span data-id="element-2093">{pivotBy}</span>
                    <ChevronDownIcon size={14} data-id="element-2094" />
                  </button>
                  {showPivotDropdown && <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-id="element-2095">
                      {(['Payment method', 'Card brand', 'Payment method category', 'Funding source type'] as PivotOption[]).map(option => <button key={option} onClick={() => {
                    setPivotBy(option);
                    setShowPivotDropdown(false);
                  }} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${pivotBy === option ? 'text-blue-600 font-medium' : 'text-gray-700'}`} data-id="element-2096">
                          {option}
                        </button>)}
                    </div>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-2097">
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2098">
                <DownloadIcon size={14} data-id="element-2099" />
                {t('components.CardPaymentsBreakdown.download')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-2100">
            {metrics.map(metric => <button key={metric.id} onClick={() => setSelectedMetric(metric.id as KeyMetric)} className={`text-left p-4 rounded-lg border-2 transition-all ${selectedMetric === metric.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2101">
                <div className="flex items-center gap-1 mb-2" data-id="element-2102">
                  <span className="text-sm text-gray-600" data-id="element-2103">{metric.title}</span>
                </div>
                <div className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-2104">
                  {metric.value}
                </div>
                <div className={`text-sm ${metric.change.startsWith('-') ? 'text-red-600' : 'text-green-600'}`} data-id="element-2105">
                  {metric.change}
                </div>
              </button>)}
          </div>

          <div className="h-80 w-full mt-8" role="img" aria-label="Payment methods metric chart" data-id="element-2106">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2107">
              {selectedMetric === 'average-order-value' ? <LineChart data={chartData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-2108">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2109" />

                  <XAxis dataKey="date" tickFormatter={dateFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-2110" />

                  <YAxis tickFormatter={yAxisFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dx={-10} data-id="element-2111" />

                  <Tooltip content={<CustomTooltip data-id="element-2113" />} cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 2
              }} data-id="element-2112" />

                  {pivotCategories.map(cat => <Line key={cat.key} type="monotone" dataKey={cat.key} name={cat.label} stroke={colorMap[cat.key]} strokeWidth={2} dot={false} activeDot={{
                r: 4,
                fill: colorMap[cat.key],
                stroke: '#fff',
                strokeWidth: 2
              }} data-id="element-2114" />)}
                </LineChart> : <BarChart data={chartData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-2115">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2116" />

                  <XAxis dataKey="date" tickFormatter={dateFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-2117" />

                  <YAxis tickFormatter={yAxisFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dx={-10} data-id="element-2118" />

                  <Tooltip content={<CustomTooltip data-id="element-2120" />} cursor={{
                fill: '#F3F4F6'
              }} data-id="element-2119" />

                  {pivotCategories.map((cat, i) => <Bar key={cat.key} dataKey={cat.key} name={cat.label} stackId="a" fill={colorMap[cat.key]} radius={i === pivotCategories.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]} data-id="element-2121" />)}
                </BarChart>}
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-center gap-6 mt-6" data-id="element-2122">
            {pivotCategories.map(cat => <div key={cat.key} className="flex items-center gap-2" data-id="element-2123">
                <div className="w-3 h-3 rounded-full" style={{
              backgroundColor: colorMap[cat.key]
            }} data-id="element-2124" />

                <span className="text-xs text-gray-600" data-id="element-2125">{cat.label}</span>
              </div>)}
          </div>
        </div>

        {/* Breakdown Table - inside same card */}
        <div className="border-t border-gray-200 mt-6" data-id="element-2126">
          <div className="overflow-x-auto" data-id="element-2127">
            <table className="w-full text-sm text-left" data-id="element-2128">
              <thead className="text-xs text-gray-500 bg-gray-50 border-y border-gray-200" data-id="element-2129">
                <tr data-id="element-2130">
                  <th className="px-4 py-3 font-medium" data-id="element-2131">
                    {pivotBy === 'Payment method category' ? 'Category' : pivotBy === 'Funding source type' ? 'Funding source' : pivotBy}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2132">
                    {t('components.PaymentVolumeWidget.volume')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2133">
                    {t('components.BreakdownTable.share_of_volume')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2134">
                    {t('components.DisputesFilters.payment_count')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2135">
                    {t('components.PaymentMethodsTab.share_of_count')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2136">
                    {t('components.PaymentMethodsTab.avg_order_value')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2137">
                    {t('components.BreakdownTable.change')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-2138">
                {breakdownRows.map((row, index) => <tr key={index} className={row.isTotal ? 'bg-gray-50 font-medium' : 'hover:bg-gray-50'} data-id="element-2139">
                    <td className="px-4 py-3 text-gray-900" data-id="element-2140">
                      <div className="flex items-center gap-2" data-id="element-2141">
                        {!row.isTotal && pivotCategories[index] && <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                      backgroundColor: colorMap[pivotCategories[index]?.key]
                    }} data-id="element-2142" />}
                        {row.category}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900" data-id="element-2143">
                      {row.volume}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500" data-id="element-2144">
                      {row.share}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900" data-id="element-2145">
                      {row.paymentCount}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500" data-id="element-2146">
                      {row.countShare}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900" data-id="element-2147">
                      {row.aov}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-2148">
                      {row.change && <span className={row.change.startsWith('+') ? 'text-green-600' : 'text-red-600'} data-id="element-2149">
                          {row.change}
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top Countries Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-2150">
        <div className="p-6 pb-0" data-id="element-2151">
          <div className="flex items-center justify-between mb-4" data-id="element-2152">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-2153">
              {t('components.PaymentMethodsTab.top_countries')}
            </h2>
            <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2154">
              <DownloadIcon size={14} data-id="element-2155" />
              {t('components.CardPaymentsBreakdown.download')}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-2156">
            {countryMetrics.map(metric => <button key={metric.id} onClick={() => setSelectedCountryMetric(metric.id as CountryMetric)} className={`text-left p-4 rounded-lg border-2 transition-all ${selectedCountryMetric === metric.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2157">
                <div className="flex items-center gap-1 mb-2" data-id="element-2158">
                  <span className="text-sm text-gray-600" data-id="element-2159">{metric.title}</span>
                </div>
                <div className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-2160">
                  {metric.value}
                </div>
                <div className={`text-sm ${metric.change.startsWith('-') ? 'text-red-600' : 'text-green-600'}`} data-id="element-2161">
                  {metric.change}
                </div>
              </button>)}
          </div>

          {/* Horizontal Bar Chart */}
          <div className="h-72 w-full mt-4" role="img" aria-label="Payments by country chart" data-id="element-2162">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2163">
              <BarChart data={countryChartData} layout="vertical" margin={{
              top: 0,
              right: 40,
              left: 0,
              bottom: 0
            }} data-id="element-2164">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" data-id="element-2165" />

                <XAxis type="number" tickFormatter={countryBarFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} data-id="element-2166" />

                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#374151',
                fontSize: 13
              }} width={120} tickFormatter={(value: string) => {
                const country = countryChartData.find(c => c.name === value);
                return country ? `${country.flag} ${value}` : value;
              }} data-id="element-2167" />

                <Tooltip content={<CountryBarTooltip data-id="element-2169" />} cursor={{
                fill: '#F3F4F6'
              }} data-id="element-2168" />

                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={28} data-id="element-2170">
                  {countryChartData.map((_, index) => <rect key={index} fill={COUNTRY_COLORS[index % COUNTRY_COLORS.length]} data-id="element-2171" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Country Breakdown Table */}
        <div className="border-t border-gray-200 mt-6" data-id="element-2172">
          <div className="overflow-x-auto" data-id="element-2173">
            <table className="w-full text-sm text-left" data-id="element-2174">
              <thead className="text-xs text-gray-500 bg-gray-50 border-y border-gray-200" data-id="element-2175">
                <tr data-id="element-2176">
                  <th className="px-4 py-3 font-medium" data-id="element-2177">
                    {t('components.PaymentMethodsTab.country')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2178">
                    {t('components.PaymentVolumeWidget.volume')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2179">
                    {t('components.BreakdownTable.share_of_volume')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2180">
                    {t('components.DisputesFilters.payment_count')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2181">
                    {t('components.PaymentMethodsTab.share_of_count')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2182">
                    {t('components.PaymentMethodsTab.avg_order_value')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2183">
                    {t('components.BreakdownTable.change')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-2184">
                {countryBreakdownRows.map((row, index) => <Fragment key={index} data-id="element-2185">
                    <tr className={row.isTotal ? 'bg-gray-50 font-medium' : row.country === 'Others' ? 'hover:bg-gray-50 cursor-pointer' : 'hover:bg-gray-50'} onClick={row.country === 'Others' ? () => setShowOtherCountries(!showOtherCountries) : undefined} data-id="element-2186">
                      <td className="px-4 py-3 text-gray-900" data-id="element-2187">
                        <div className="flex items-center gap-2" data-id="element-2188">
                          {!row.isTotal && <span className="text-base" data-id="element-2189">{row.flag}</span>}
                          {row.country}
                          {row.country === 'Others' && <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${showOtherCountries ? 'rotate-180' : ''}`} data-id="element-2190" />}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900" data-id="element-2191">
                        {row.volume}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500" data-id="element-2192">
                        {row.volumeShare}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900" data-id="element-2193">
                        {row.count}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500" data-id="element-2194">
                        {row.countShare}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900" data-id="element-2195">
                        {row.aov}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-2196">
                        {row.change && <span className={row.change.startsWith('+') ? 'text-green-600' : 'text-red-600'} data-id="element-2197">
                            {row.change}
                          </span>}
                      </td>
                    </tr>
                    {row.country === 'Others' && showOtherCountries && OTHER_COUNTRIES.map((sub, subIdx) => <tr key={`other-${subIdx}`} className="bg-gray-50/50" data-id="element-2198">
                          <td className="px-4 py-2.5 text-gray-700" data-id="element-2199">
                            <div className="flex items-center gap-2 pl-6" data-id="element-2200">
                              <span className="text-base" data-id="element-2201">{sub.flag}</span>
                              {sub.label}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-700" data-id="element-2202">
                            {sub.volume >= 1000000 ? `${getTenant().currency.code} ${(sub.volume / 1000000).toFixed(2)}Mn` : `${getTenant().currency.code} ${(sub.volume / 1000).toFixed(0)}K`}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-400" data-id="element-2203">
                            {`${(sub.volumeShare * 100).toFixed(1)}%`}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-700" data-id="element-2204">
                            {sub.count.toLocaleString()}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-400" data-id="element-2205">
                            {`${(sub.countShare * 100).toFixed(1)}%`}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-700" data-id="element-2206">
                            {`${getTenant().currency.code} ${sub.aov}`}
                          </td>
                          <td className="px-4 py-2.5 text-right" data-id="element-2207">
                            <span className={sub.change.startsWith('+') ? 'text-green-600' : 'text-red-600'} data-id="element-2208">
                              {sub.change}
                            </span>
                          </td>
                        </tr>)}
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}